/**
 * DirectorConsentLauncher
 *
 * Botón que el director (o admin) usa para abrir el `ConsentCheckinModal`
 * en nombre de un inscrito específico. Carga member + person bajo demanda
 * para evitar fetches innecesarios al renderizar tablas grandes.
 *
 * Útil para miembros que no tienen cuenta de usuario propia: el director
 * realiza el check-in presencialmente con ellos.
 */
import { useEffect, useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import Button from "@/components/ui/Button";
import ConsentCheckinModal from "./ConsentCheckinModal";
import { memberService } from "@/lib/services/members";
import type { Event, EventRegistration, Member, Person } from "@/types";

interface Props {
  event: Event;
  registration: EventRegistration;
  clubName: string;
  associationName: string;
  churchName?: string;
  districtName?: string;
  /** Texto del botón. */
  label?: string;
  /** Variante del botón. */
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Si true, no renderiza el botón; expone solo el modal controlado por `open`. */
  controlled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCompleted?: () => void;
}

export function DirectorConsentLauncher({
  event,
  registration,
  clubName,
  associationName,
  churchName,
  districtName,
  label = "Check-in",
  variant = "secondary",
  size = "sm",
  className,
  controlled,
  open: openProp,
  onOpenChange,
  onCompleted,
}: Props) {
  const [openInternal, setOpenInternal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState<Member | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = controlled ? !!openProp : openInternal;
  const setOpen = (v: boolean) => {
    if (controlled) onOpenChange?.(v);
    else setOpenInternal(v);
  };

  const handleOpen = async () => {
    if (!registration.memberId) {
      setError("Inscripción sin memberId");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await memberService.getByIdWithPerson(
        registration.memberId,
      );
      if (!result) {
        setError("No se encontró el miembro");
        return;
      }
      const { person: p, ...m } = result;
      setMember(m as Member);
      setPerson(p);
      setOpen(true);
    } catch (e) {
      console.error("[DirectorConsentLauncher] error", e);
      setError("No se pudo cargar el miembro");
    } finally {
      setLoading(false);
    }
  };

  // En modo controlado el botón externo solo cambia `open`; cargar member+person
  // bajo demanda cuando se abre, igual que hace el botón interno con handleOpen.
  useEffect(() => {
    if (controlled && openProp && !member && !person && !loading && !error) {
      void handleOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled, openProp]);

  return (
    <>
      {!controlled && (
        <Button
          variant={variant}
          size={size}
          onClick={handleOpen}
          disabled={loading}
          className={className}
          title={`Hacer check-in de ${registration.personName}`}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <ClipboardCheck className="w-4 h-4 mr-1" />
          )}
          {label}
        </Button>
      )}

      {error && !open && <p className="text-xs text-red-600 mt-1">{error}</p>}

      {open && member && person && (
        <ConsentCheckinModal
          isOpen={open}
          onClose={() => setOpen(false)}
          event={event}
          registration={registration}
          member={member}
          person={person}
          clubName={clubName}
          associationName={associationName}
          churchName={churchName}
          districtName={districtName}
          canDismiss
          onCompleted={() => {
            onCompleted?.();
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

export default DirectorConsentLauncher;
